import type { NestedGroupFilter } from '@/components/filters/ComplexFilterNestedGroup.vue';
import type {
  FilterGroupOutput,
  GroupedFieldOption,
} from '@/components/filters/types';

import useGetPicklistFieldOptions from '@/composables/useGetPicklistFieldOptions';
import { getSourceTableLabel } from '@/constants/data_sources';
import { requiresSequentialOperand } from '@/constants/filter-operators';
import type { FilterFieldOption, SelectOption } from '@/types/filters';
import type { InternalFilterGroup } from '@/types/populations';
import type { Source, SourceDataType } from '@/types/sources';

type ExtendedSourceField = {
  id?: number;
  column: string;
  display_name?: string;
  nickname?: string | null;
  data_type?: SourceDataType;
  is_visible?: boolean;
  is_filterable?: boolean;
  syncing_disabled_reason?: string | null;
  options?: Record<string, string | number> | null;
};

export type FieldOptionsContext = {
  dataSourceType: string;
  offlinePartnerUuid?: string;
};

function createFetchOptions(
  fieldId: number,
  fieldOptions: Record<string, string | number> | null,
  context: FieldOptionsContext,
): (query: string) => Promise<SelectOption[]> {
  return async (query: string): Promise<SelectOption[]> => {
    const results = await useGetPicklistFieldOptions({
      payload: {
        query,
        field: fieldId,
        offline_partner_org_uuid: context.offlinePartnerUuid,
      },
      fieldOptions: fieldOptions ?? {},
      dataSourceType: context.dataSourceType,
    });

    return results.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
  };
}

/**
 * Transforms Source[] into GroupedFieldOption[] for ComplexFilter.
 */
export function sourcesToFieldOptions(
  sources: Source[],
  context?: FieldOptionsContext,
): GroupedFieldOption<number>[] {
  return sources
    .filter((source) => source.fields?.length)
    .map((source) => {
      const extendedFields = source.fields as unknown as ExtendedSourceField[];

      return {
        id: source.table,
        label: getSourceTableLabel(source.table),
        useBorder: true,
        options: extendedFields
          .filter((field) => {
            const hasId = field.id != null;
            const notInternal = !field.column?.startsWith('_sdc_');
            const isVisible = field.is_visible !== false;
            const isFilterable = field.is_filterable !== false;
            const notDisabled = !field.syncing_disabled_reason;
            return (
              hasId && notInternal && isVisible && isFilterable && notDisabled
            );
          })
          .map(
            (field): FilterFieldOption<number> => ({
              label: field.display_name || field.nickname || field.column,
              value: field.id as number,
              dataType: field.data_type ?? 'string',
              ...(context &&
                field.data_type !== 'boolean' && {
                  fetchOptions: createFetchOptions(
                    field.id as number,
                    field.options ?? null,
                    context,
                  ),
                }),
            }),
          )
          .sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
          ),
      };
    })
    .filter((group) => group.options.length > 0);
}

// Operators that don't require a comparison value
const NO_OPERAND_OPERATORS = new Set(['is empty', 'is not empty']);

function isCompleteFilter(f: NestedGroupFilter | null | undefined): boolean {
  if (!f || f.value === undefined || f.operator === undefined) return false;
  if (NO_OPERAND_OPERATORS.has(f.operator)) return true;
  return (
    f.operand !== undefined &&
    f.operand !== null &&
    (!Array.isArray(f.operand) || f.operand.length > 0)
  );
}

/**
 * Converts FilterGroupOutput[] (advanced filter UI shape) to InternalFilterGroup[] (legacy/API shape).
 */
export function advancedFiltersToInternal(
  groups: FilterGroupOutput[],
): InternalFilterGroup[] {
  if (!groups?.length) return [];

  const result: InternalFilterGroup[] = [];

  for (const group of groups) {
    if (!group?.filters?.length || !group?.expression?.length) continue;

    result.push({
      // tableName is the API key for source_filter_expressions and must be the
      // raw source table (group.sourceId), not the display label (group.title).
      tableName: String(group.sourceId),
      mdmType: '',
      filter_parts: group.filters.map((f) => ({
        label: f.label,
        source_field_id: f.value as number,
        operator: f.operator as string,
        operand: normalizeOperandForApi(f.operator as string, f.operand),
      })),
      filter_expression: group.expression,
    });
  }

  return result;
}

/**
 * Converts InternalFilterGroup[] (legacy/API shape) to FilterGroupOutput[] (advanced filter UI shape).
 * Used to sync legacy filter state to advanced filter UI.
 */
export function internalFiltersToAdvanced(
  groups: InternalFilterGroup[],
): FilterGroupOutput[] {
  if (!groups?.length) return [];

  const result: FilterGroupOutput[] = [];

  for (const group of groups) {
    if (!group?.filter_parts?.length || !group.tableName) continue;

    const filters: NestedGroupFilter[] = group.filter_parts.map((part) => ({
      label: part.label || '',
      value: part.source_field_id as number,
      operator: part.operator as NestedGroupFilter['operator'],
      operand: part.operand,
    }));

    // Use existing expression or generate default (join all filter labels with AND)
    let expression = group.filter_expression;
    if (!expression?.length && filters.length > 0) {
      expression = filters.reduce<string[]>((acc, filter, index) => {
        if (index > 0) acc.push('AND');
        acc.push(filter.label);
        return acc;
      }, []);
    }

    result.push({
      title: group.tableName,
      sourceId: group.tableName,
      filters,
      expression: expression ?? [],
    });
  }

  return result;
}

/**
 * Checks if all filters in the groups are complete.
 */
export function areAllFiltersComplete(groups: FilterGroupOutput[]): boolean {
  if (!groups?.length) return true;

  for (const group of groups) {
    if (!group?.filters?.length) continue;

    for (const filter of group.filters) {
      if (!isCompleteFilter(filter)) return false;
    }
  }

  return true;
}

/**
 * Converts population.source_filter_expressions (API shape) to FilterGroupOutput[] (advanced filter UI shape).
 * Used to populate the advanced filter UI when editing existing populations.
 */
export function populationFiltersToAdvanced(
  sourceFilterExpressions:
    | Record<
        string,
        {
          filter_expression?: string[];
          filter_parts?: {
            label: string;
            operand: unknown;
            operator: string;
            source_field_id: number;
          }[];
        }
      >
    | null
    | undefined,
): FilterGroupOutput[] {
  if (!sourceFilterExpressions) return [];

  const tableNames = Object.keys(sourceFilterExpressions);
  if (!tableNames.length) return [];

  const result: FilterGroupOutput[] = [];

  for (const tableName of tableNames) {
    const filterData = sourceFilterExpressions[tableName];
    if (!filterData?.filter_parts?.length) continue;

    const filters: NestedGroupFilter[] = filterData.filter_parts.map(
      (part) => ({
        label: part.label || '',
        value: part.source_field_id,
        operator: part.operator as NestedGroupFilter['operator'],
        operand: part.operand,
      }),
    );

    // Generate default expression if missing - join all filter labels with AND
    let expression = filterData.filter_expression;
    if (!expression?.length && filters.length > 0) {
      expression = filters.reduce<string[]>((acc, filter, index) => {
        if (index > 0) acc.push('AND');
        acc.push(filter.label);
        return acc;
      }, []);
    }

    // sourceId must be the table name (string) to match GroupedFieldOption.id
    // which is set from source.table in sourcesToFieldOptions
    result.push({
      title: tableName,
      sourceId: tableName,
      filters,
      expression: expression ?? [],
    });
  }

  return result;
}

/**
 * Converts FilterGroupOutput[] to source_filter_expressions format for the API.
 * Only includes complete filters and ensures expression only references existing filters.
 */
export function advancedFiltersToSourceExpressions(
  groups: FilterGroupOutput[],
): Record<
  string,
  {
    filter_expression: string[];
    filter_parts: {
      label: string;
      operand: unknown;
      operator: string;
      source_field_id: number;
    }[];
  }
> {
  const result: Record<
    string,
    {
      filter_expression: string[];
      filter_parts: {
        label: string;
        operand: unknown;
        operator: string;
        source_field_id: number;
      }[];
    }
  > = {};

  for (const group of groups) {
    if (!group?.filters?.length || !group?.expression?.length) continue;

    // Only include complete filters (have value and operator)
    const completeFilters = group.filters.filter(isCompleteFilter);
    if (!completeFilters.length) continue;

    const completeLabels = new Set(completeFilters.map((f) => f.label));

    // Clean expression to only reference complete filters
    const cleanedExpression = cleanExpression(group.expression, completeLabels);
    if (!cleanedExpression.length) continue;

    const filterParts = completeFilters.map((f) => ({
      label: f.label,
      source_field_id: f.value as number,
      operator: f.operator as string,
      operand: normalizeOperandForApi(f.operator as string, f.operand),
    }));

    // Key by the raw source table (group.sourceId), not the display label
    // (group.title) - this is the source_filter_expressions key the API expects.
    result[String(group.sourceId)] = {
      filter_expression: cleanedExpression,
      filter_parts: filterParts,
    };
  }

  return result;
}

/**
 * `in` / `not in` / contains ops require a non-empty sequence on the API
 * (Malli `[:sequential {:min 1} Primitive]`). Comparative ops require a scalar,
 * so an array carried over from a previously-sequential operator must be
 * unwrapped or the API rejects the whole payload.
 */
function normalizeOperandForApi(operator: string, operand: unknown): unknown {
  if (operand === null || operand === undefined) return operand;

  if (requiresSequentialOperand(operator)) {
    return Array.isArray(operand) ? operand : [operand];
  }

  if (!Array.isArray(operand)) return operand;
  return operand.length ? operand[0] : null;
}

/**
 * Cleans an expression to only include labels that exist in the validLabels set.
 * Removes orphaned AND/OR operators.
 */
function cleanExpression(
  expression: string[],
  validLabels: Set<string>,
): string[] {
  const result: string[] = [];

  for (const token of expression) {
    if (token === 'AND' || token === 'OR') {
      // Only add operator if we have a previous item and it's not an operator
      if (
        result.length > 0 &&
        !['AND', 'OR'].includes(result[result.length - 1] ?? '')
      ) {
        result.push(token);
      }
    } else if (validLabels.has(token)) {
      // Valid label - add it (operator before it is kept)
      result.push(token);
    } else if (
      result.length > 0 &&
      ['AND', 'OR'].includes(result[result.length - 1] ?? '')
    ) {
      // Invalid label - remove any trailing operator
      result.pop();
    }
  }

  // Remove trailing operator if exists
  if (
    result.length > 0 &&
    ['AND', 'OR'].includes(result[result.length - 1] ?? '')
  ) {
    result.pop();
  }

  return result;
}
